/**
 * YouTube helper functions
 */

import type { YouTubeVideoData } from '../../types/video';

/**
 * Extract YouTube video ID from various URL formats
 */
export const extractYouTubeId = (url: string): string | null => {
  const patterns = [
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/watch\?v=([^&]+)/,
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/embed\/([^?]+)/,
    /(?:https?:\/\/)?(?:www\.)?youtu\.be\/([^?]+)/,
    /(?:https?:\/\/)?(?:www\.)?youtube\.com\/v\/([^?]+)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }

  // If no pattern matches, check if it's already just an ID
  if (url.length === 11 && !url.includes('/')) {
    return url;
  }

  return null;
};

/**
 * Fetch video metadata from YouTube API
 */
export const fetchYouTubeMetadata = async (
  videoId: string,
  apiKey: string
): Promise<YouTubeVideoData | null> => {
  try {
    const response = await fetch(
      `https://www.googleapis.com/youtube/v3/videos?id=${videoId}&part=snippet,contentDetails,statistics&key=${apiKey}`
    );

    if (!response.ok) {
      throw new Error('Failed to fetch YouTube data');
    }

    const data = await response.json();
    
    if (data.items && data.items.length > 0) {
      return data.items[0];
    }

    return null;
  } catch (_error) {
    // Error fetching YouTube metadata
    return null;
  }
};

/**
 * Convert ISO 8601 duration to seconds
 */
export const parseDuration = (duration: string): number => {
  const match = duration.match(/PT(\d+H)?(\d+M)?(\d+S)?/);
  
  if (!match) return 0;

  const hours = parseInt(match[1]?.slice(0, -1) || '0');
  const minutes = parseInt(match[2]?.slice(0, -1) || '0');
  const seconds = parseInt(match[3]?.slice(0, -1) || '0');

  return hours * 3600 + minutes * 60 + seconds;
};

/**
 * Get the best available thumbnail from YouTube data
 */
export const getBestThumbnail = (thumbnails: any): string | undefined => {
  return thumbnails.maxres?.url ||
         thumbnails.standard?.url ||
         thumbnails.high?.url ||
         thumbnails.medium?.url ||
         thumbnails.default?.url;
};

/**
 * Extract video metadata from YouTube API response
 */
export const extractVideoMetadata = (youtubeData: YouTubeVideoData, formTags: string[] = []) => {
  const thumbnails = youtubeData.snippet.thumbnails;
  const bestThumbnail = getBestThumbnail(thumbnails);
  
  return {
    title: youtubeData.snippet.title,
    description: youtubeData.snippet.description,
    thumbnail_url: bestThumbnail,
    thumbnail_medium: thumbnails.medium?.url || thumbnails.default?.url,
    thumbnail_high: thumbnails.high?.url || thumbnails.medium?.url || thumbnails.default?.url,
    duration: youtubeData.contentDetails?.duration,
    duration_seconds: youtubeData.contentDetails?.duration 
      ? parseDuration(youtubeData.contentDetails.duration)
      : undefined,
    published_at: new Date(youtubeData.snippet.publishedAt),
    channel_id: youtubeData.snippet.channelId,
    channel_title: youtubeData.snippet.channelTitle,
    view_count: parseInt(youtubeData.statistics?.viewCount || '0'),
    like_count: parseInt(youtubeData.statistics?.likeCount || '0'),
    tags: [...formTags, ...(youtubeData.snippet.tags || [])],
  };
};